import React, { useMemo } from 'react'
import { mainAppBarId } from '@e3dc-react/shell/Elements/Header/HeaderBar'
import { GuidedTourStep } from '@e3dc-react/shell/Elements/GuidedTour'
import { Typography, useTheme } from '@mui/material'
import { Styles } from 'react-joyride'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useTourGuideStyles } from '@e3dc-react/shell/Elements/GuidedTour/use-tour-guide.styles'
import { useStickyBarHeight } from '@e3dc-react/shell/Libs/Header'

const XS_TOOLTIP_WIDTH = 360
const Z_INDEX_ABOVE_NAVBAR = 1202

/** our guide defined steps with real content components */
export const useDashboardTourSteps = (): GuidedTourStep[] => {
  const { t } = useCustomTranslation()
  const defaultStyles = useTourGuideStyles()
  const dashboardStyles = useDashboardTourGuideStyles()

  const { stickyBarHeight } = useStickyBarHeight()
  const datebarHeight = stickyBarHeight ? stickyBarHeight + 12 : undefined

  return useMemo(
    () => [
      {
        target: `#${mainAppBarId}`,
        title: <Typography variant="h3">{t('dashboard.tour.welcome-step-title')}</Typography>,
        content: <Typography variant="body2">{t('dashboard.tour.welcome-step-content')}</Typography>,
        placement: 'center',
        styles: { ...defaultStyles, ...dashboardStyles },
        zIndex: Z_INDEX_ABOVE_NAVBAR,
      },
      {
        target: '#dashboard-liveview',
        title: <Typography variant="h3">{t('dashboard.tour.liveview-step-title')}</Typography>,
        content: <Typography variant="body2">{t('dashboard.tour.liveview-step-content')}</Typography>,
        placement: 'right-start',
        styles: { ...defaultStyles, ...dashboardStyles, tooltip: { width: XS_TOOLTIP_WIDTH } },
        zIndex: Z_INDEX_ABOVE_NAVBAR,
      },
      {
        target: '#dashboard-statistics',
        title: <Typography variant="h3">{t('dashboard.tour.statistics-step-title')}</Typography>,
        content: <Typography variant="body2">{t('dashboard.tour.statistics-step-content')}</Typography>,
        placement: 'auto',
        styles: { ...defaultStyles, ...dashboardStyles },
        zIndex: Z_INDEX_ABOVE_NAVBAR,
        spotlightPadding: { top: datebarHeight },
        scrollOffset: 250,
      },
      {
        target: '#framework-system-properties',
        title: <Typography variant="h3">{t('dashboard.tour.navigation-step-title')}</Typography>,
        content: <Typography variant="body2">{t('dashboard.tour.navigation-step-content')}</Typography>,
        placement: 'auto',
        styles: { ...defaultStyles, ...dashboardStyles },
        zIndex: Z_INDEX_ABOVE_NAVBAR,
        skipScroll: true,
      },
    ],
    [t, defaultStyles, dashboardStyles, datebarHeight]
  )
}

const useDashboardTourGuideStyles = (): Partial<Styles> => {
  const theme = useTheme()
  return {
    tooltipTitle: {
      padding: theme.spacing(2),
    },
    tooltipContent: {
      padding: theme.spacing(2),
    },
    buttonClose: {
      padding: theme.spacing(4),
    },
  }
}
